import { usePage } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import { ButtonAnchor } from "@/components/ui/Button";
import formatTime from "@/utils/formatTime";
import type { SharedProps } from "@/types";

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

// Hackatime is the only time source, so an unlinked account can log every hour
// it likes and ship nothing. The link used to live solely in forced onboarding,
// which organizers who signed up for Boba Bash never see - so it lives here too,
// on the pages where the hours are actually spent.
export default function HackatimeConnect({
  compact = false,
}: {
  compact?: boolean;
}) {
  const { props } = usePage<SharedProps>();
  const linked = !!props.user?.hackatime_id;
  const rate = props.program.budget_per_hour_cents;

  if (linked && compact) return null;

  return (
    <Panel title="hackatime.link" tone={linked ? "brand" : "alt"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-subheading text-ink font-bold">
              Hackatime
            </h2>
            {linked ? (
              <Badge tone="success">Connected</Badge>
            ) : (
              <Badge tone="warning">Not connected</Badge>
            )}
          </div>

          {linked ? (
            <p className="text-caption text-ink-muted">
              {formatTime(props.user.total_reported_seconds)} logged so far.
              Attach the projects you want counted when you ship.
            </p>
          ) : (
            <p className="text-body text-ink-muted max-w-prose">
              Hackatime counts the hours you spend coding. Connect it, ship what
              you build, and every approved hour adds{" "}
              <strong className="text-ink">{money(rate)}</strong> to your
              event's budget.
            </p>
          )}
        </div>

        <ButtonAnchor
          href="/hackatime/link"
          variant={linked ? "secondary" : "primary"}
          size={linked ? "sm" : "md"}
          className="shrink-0"
        >
          {linked ? "Reconnect" : "Connect Hackatime"}
        </ButtonAnchor>
      </div>
    </Panel>
  );
}
